// StockTable (truck stock, BL-203): the Stock page's table, drawn from stock.levels' answer
// (stock-schemas.ts). It replaces the Stock page's one-off "Stock table and facts" table. One place: an
// item a row, by category (LineTable's groups, each saying how many and how many are below min), with its
// code, what's on hand, its min and max, and when it was last counted; at our cost too, for whoever sees
// our cost (the answer carries costCents only for them). Every place: an item a row and a place a column,
// what's on hand in each. A level is said in words where it matters: "below min" or "below zero" beside the
// figure, in ink after StateMark's red bar (it needs someone; red is never small text here), "no min"
// (nothing refills it) and "to count" dashed (waiting, DESIGN.md §2), "never" when it was never counted. An
// item's name opens its sheet; a place's name opens the place. While a tech's count is with the office
// (blind), his own van's figures say "hidden". Narrow (a phone), one place drops Last counted and At cost
// (the item's sheet has them) and says "to count" under the item's name; so does one place whose answer
// says nothing of when it was last counted (lastCounted absent), with no Last counted column. Reads only --stocktable-*; LineTable and
// Link read their own.
// New (Domain/Stock), not a LineTable variant: it is LineTable's groups drawn from stock.levels' answer
// with a level's words (below min, no min, to count, never), and it promotes the page's one-off table.
import type { ReactNode } from 'react';
import { money } from '../../format.ts';
import type { StockItemRow, StockLevel, StockPlace } from '../../stock-schemas.ts';
import { LineTable, type LineCell, type LineColumn, type LineGroup } from '../LineTable/LineTable.tsx';
import { Link } from '../Link/Link.tsx';
import { StateMark } from '../StateMark/StateMark.tsx';
import { hasNoMin, isBelowMin, qtyWords } from '../stock-words.ts';
import './StockTable.css';

export interface StockTableProps {
  /** One place: its own columns. Several: a column each (Every place). */
  places: StockPlace[];
  rows: StockItemRow[];
  /** Opens an item's sheet (its levels, min and max); without it the names are words. */
  onOpenItem?: (itemId: string) => void;
  /** Every place: opens one place. */
  onOpenPlace?: (placeId: string) => void;
  /** The tech's own van while his count is with the office: on hand says "hidden". */
  isHidden?: boolean;
  /** No rows: what it says ("Nothing on Riley’s truck yet."). */
  empty?: ReactNode;
  /** Narrow (a phone), one place: no Last counted or At cost column (the item's sheet has them); "to count"
   *  moves under the item's name, so on hand stays narrow and the table fits the width. */
  narrow?: boolean;
}

const Wait = ({ children }: { children: ReactNode }) => (
  <span className="fl-stocktable__wait">{children}</span>
);
const Mark = ({ children }: { children: ReactNode }) => (
  <span className="fl-stocktable__mark">{children}</span>
);

function onHand(l: StockLevel, unit: string, every: boolean): LineCell {
  const q = qtyWords(l.onHand, unit);
  if (Number(l.onHand) < 0)
    return {
      tone: 'problem',
      value: (
        <>
          <Mark>below zero</Mark>
          <strong>{q}</strong>
        </>
      ),
    };
  if (isBelowMin(l))
    return {
      tone: 'problem',
      value: (
        <>
          <Mark>{every ? `below ${qtyWords(l.min, unit)}` : 'below min'}</Mark>
          <strong>{q}</strong>
        </>
      ),
    };
  if (!every) return q;
  return (
    <>
      {hasNoMin(l) ? (
        <Mark>
          <Wait>no min</Wait>
        </Mark>
      ) : null}
      {l.countNeeded ? (
        <Mark>
          <Wait>to count</Wait>
        </Mark>
      ) : null}
      {q}
    </>
  );
}

/** An item's name (opening its sheet) and code; narrow, "to count" (dashed: it waits) after the code. */
function itemCell(r: StockItemRow, onOpenItem?: (id: string) => void, toCount?: boolean): ReactNode {
  return (
    <span className="fl-stocktable__item">
      {onOpenItem ? (
        <Link onPress={() => onOpenItem(r.itemId)}>{r.itemName}</Link>
      ) : (
        <span className="fl-stocktable__name">{r.itemName}</span>
      )}
      <span className="fl-stocktable__code">{r.code}</span>
      {toCount ? <Wait>to count</Wait> : null}
    </span>
  );
}

/** Rows by category, in the order they come; each head says how many and how many are below min. */
function byCategory(
  rows: StockItemRow[],
  place: string | null,
  draw: (r: StockItemRow) => Record<string, LineCell>,
) {
  const groups = new Map<string, { name: string; rows: StockItemRow[] }>();
  for (const r of rows) {
    const g = groups.get(r.category.id) ?? { name: r.category.name, rows: [] };
    g.rows.push(r);
    groups.set(r.category.id, g);
  }
  return [...groups.entries()].map(([id, g]): LineGroup => {
    const levels = place ? g.rows.map((r) => r.levels[place]).filter((l): l is StockLevel => !!l) : [];
    const below = levels.filter(isBelowMin).length;
    const noMin = levels.filter(hasNoMin).length;
    return {
      id,
      label: g.name,
      says: place ? (
        <>
          {g.rows.length}
          {below ? (
            <>
              {' · '}
              <span className="fl-stocktable__problem">
                <StateMark state="problem" />
                {below} below min
              </span>
            </>
          ) : null}
          {noMin ? ` · ${noMin} no min` : null}
        </>
      ) : undefined,
      rows: g.rows.map((r) => ({ id: r.itemId, cells: draw(r) })),
    };
  });
}

export function StockTable({
  places,
  rows,
  onOpenItem,
  onOpenPlace,
  isHidden,
  empty,
  narrow,
}: StockTableProps) {
  const one = places.length === 1 ? places[0]! : null;
  if (!rows.length)
    return (
      <p className="fl-stocktable__empty" role="status">
        {empty ?? (one ? `Nothing ${one.kind === 'truck' ? 'on' : 'in'} ${one.name} yet.` : 'No stock yet.')}
      </p>
    );
  if (one) {
    const cost = !narrow && rows.some((r) => r.levels[one.id] && 'costCents' in r.levels[one.id]!);
    // Last counted only where the answer says it (absent: not known, so no column of guesses)
    const counted = !narrow && rows.some((r) => r.levels[one.id]?.lastCounted !== undefined);
    const columns: LineColumn[] = [
      { id: 'item', label: 'Item' },
      { id: 'on', label: 'On hand', align: 'end' },
      { id: 'min', label: 'Min', align: 'end' },
      { id: 'max', label: 'Max', align: 'end' },
      ...(counted ? [{ id: 'counted', label: 'Last counted', align: 'end' } as const] : []),
      ...(cost ? [{ id: 'cost', label: 'At cost', align: 'end' } as const] : []),
    ];
    const groups = byCategory(rows, isHidden ? null : one.id, (r) => {
      const l = r.levels[one.id]!;
      const none = hasNoMin(l);
      return {
        item: itemCell(r, onOpenItem, !counted && !!l.countNeeded),
        on: isHidden ? { tone: 'none', value: 'hidden' } : onHand(l, r.unit, false),
        min: none ? <Wait>no min</Wait> : qtyWords(l.min, r.unit),
        max: none ? { tone: 'none', value: '–' } : qtyWords(l.max, r.unit),
        ...(!counted
          ? {}
          : {
              counted: (
                <>
                  {l.countNeeded ? (
                    <Mark>
                      <Wait>to count</Wait>
                    </Mark>
                  ) : null}
                  {l.lastCounted ?? <span className="fl-stocktable__muted">never</span>}
                </>
              ),
            }),
        ...(cost ? { cost: l.costCents == null ? { tone: 'none', value: '–' } : money(l.costCents) } : {}),
      };
    });
    return (
      <div className="fl-stocktable">
        <LineTable label={`Stock on ${one.name}`} columns={columns} groups={groups} />
      </div>
    );
  }
  const columns: LineColumn[] = [
    { id: 'item', label: 'Item' },
    ...places.map((p) => ({
      id: p.id,
      label: onOpenPlace ? <Link onPress={() => onOpenPlace(p.id)}>{p.name}</Link> : p.name,
      align: 'end' as const,
    })),
  ];
  const groups = byCategory(rows, null, (r) => ({
    item: itemCell(r, onOpenItem),
    ...Object.fromEntries(
      places.map((p) => {
        const l = r.levels[p.id];
        return [p.id, l ? onHand(l, r.unit, true) : { tone: 'none', value: '–' }];
      }),
    ),
  }));
  return (
    <div className="fl-stocktable">
      <LineTable label="Stock in every place" columns={columns} groups={groups} />
    </div>
  );
}
